import {
	Box,
	Button,
	Col,
	Divider,
	Grid,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui-base"

import {
	ColorInputField,
	Form,
	GetFieldValue,
	SubmissionError,
	TextAreaInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"

import { showSnackbar } from "@sembark-travel/ui-snackbar"

import { useXHR } from "@sembark-travel/xhr"

import { useMemo, useRef } from "react"

import { object as objectValidator, string as stringValidator } from "yup"

import { IOrgTag, TTagScope } from "./store"
import { TagBadge } from "./TagBadges"

const defaultInitialValues: Partial<IOrgTag> = {
	name: "",
	description: "",
	style: {
		background_color: "",
		text_color: "",
	},
}

interface TagFormProps {
	scope: TTagScope
	onSuccess?: (tag: IOrgTag) => void
	onCancel?: () => void
	initialValues?: Partial<IOrgTag>
}

function getContrastTextColor(hex?: string) {
	if (!hex) return "#ffffff"

	const color = hex.replace("#", "")

	if (color.length !== 6) return "#ffffff"

	const r = parseInt(color.substring(0, 2), 16)
	const g = parseInt(color.substring(2, 4), 16)
	const b = parseInt(color.substring(4, 6), 16)

	const brightness = (r * 299 + g * 587 + b * 114) / 1000

	return brightness > 155 ? "#111111" : "#ffffff"
}

export function TagForm({
	scope,
	onSuccess,
	onCancel,
	initialValues: propInitialValues = {},
}: TagFormProps) {
	const xhr = useXHR()

	const initialValuesRef = useRef({
		...defaultInitialValues,
		...propInitialValues,
		module: scope,
	})

	const validate = useMemo(() => {
		return validateFormValues(
			objectValidator().shape({
				name: stringValidator()
					.required("Title is required")
					.max(100, "Please use 100 or less characters"),

				description: stringValidator().nullable(),
			})
		)
	}, [])

	return (
		<Form<IOrgTag>
			initialValues={initialValuesRef.current}
			validate={validate}
			onSubmit={withServerErrors(async (values) => {
				const backgroundColor = values.style?.background_color || ""
				const payload = {
					...values,
					module: scope,
					...(backgroundColor
						? {
								style: {
									background_color: backgroundColor,
									text_color: getContrastTextColor(backgroundColor),
								},
							}
						: {}),
				}

				const response = await xhr.post("/tags", payload)

				showSnackbar("Tag created successfully.")

				onSuccess?.(response.data.data)

				return response
			})}
			subscription={{
				submitting: true,
			}}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Grid gap="4">
							<Col xs={12} md={4}>
								<TextInputField
									label="Title"
									name="name"
									type="text"
									required
									placeholder="e.g. VIP Client"
								/>
							</Col>

							<Col xs={12} md={4}>
								<ColorInputField
									placeholder="#ffffff"
									name="style.background_color"
									label="Color"
									secondaryLabel="Optional"
								/>
							</Col>

							<Col xs={12} md={3}>
								<Stack gap={"1"}>
									<Text fontWeight="semibold">Preview</Text>

									<Box>
										<GetFieldValue<string> name="name">
											{({ value: name }) => (
												<GetFieldValue<
													| {
															background_color?: string
													  }
													| undefined
												> name="style">
													{({ value: style }) => {
														const backgroundColor = style?.background_color
														const previewTag = {
															id: 0,
															name: name || "Sample Tag",
															style: {
																background_color: backgroundColor || "",
																text_color: backgroundColor
																	? getContrastTextColor(backgroundColor)
																	: "",
															},
														}
														return <TagBadge tag={previewTag} />
													}}
												</GetFieldValue>
											)}
										</GetFieldValue>
									</Box>
								</Stack>
							</Col>
						</Grid>

						<TextAreaInputField
							name="description"
							label="Description"
							placeholder="Add a short description"
							secondaryLabel="Optional"
						/>

						<Divider sm />

						<Stack gap="4">
							<SubmissionError />

							<Inline gap="4">
								<Button type="submit" disabled={submitting}>
									{submitting ? "Saving..." : "Save Tag"}
								</Button>

								{onCancel ? (
									<Button
										onClick={onCancel}
										level="tertiary"
										disabled={submitting}
									>
										Cancel
									</Button>
								) : null}
							</Inline>
						</Stack>
					</Stack>
				</form>
			)}
		</Form>
	)
}
